import { supabase } from "@/lib/supabase"
import type { LeadOrigem } from "@/mocks/admin"

const SUPABASE_URL = (import.meta as any).env?.VITE_SUPABASE_URL || ""
const SUPABASE_ANON_KEY = (import.meta as any).env?.VITE_SUPABASE_ANON_KEY || ""

export interface LeadFormData {
  nome: string
  email: string
  whatsapp: string
  empresa: string
  segmento?: string
  maior_desafio?: string
  diagnostico?: any | null
  lgpd_aceito?: boolean
}

export async function saveLead(data: LeadFormData): Promise<{ success: boolean; error?: string }> {
  if (!SUPABASE_URL || !SUPABASE_ANON_KEY) {
    return { success: false, error: "VITE_SUPABASE_URL or VITE_SUPABASE_ANON_KEY not set" }
  }

  const origem: LeadOrigem = data.diagnostico ? "diagnostico" : "programa"

  try {
    const { createClient } = await import("@supabase/supabase-js")
    const client = createClient(SUPABASE_URL, SUPABASE_ANON_KEY, {
      global: { fetch: (url, options) => {
        const controller = new AbortController()
        const timeout = setTimeout(() => controller.abort(), 10000)
        return fetch(url, { ...options, signal: controller.signal }).finally(() => clearTimeout(timeout))
      }}
    })

    // Verifica se já existe lead com este email (case-insensitive)
    const { data: existente } = await client
      .from("leads")
      .select("id, origens, nome, whatsapp, empresa, segmento, maior_desafio, diagnostico")
      .ilike("email", data.email)
      .maybeSingle()

    if (existente) {
      // Adiciona origem se ainda não tiver
      const origensAtuais: string[] = existente.origens ?? []
      const origensUnicas = origensAtuais.includes(origem) ? origensAtuais : [...origensAtuais, origem]

      const { error } = await client
        .from("leads")
        .update({
          origens: origensUnicas,
          nome: data.nome || existente.nome,
          whatsapp: (data.whatsapp || existente.whatsapp || "").replace(/\D/g, ""),
          empresa: data.empresa || existente.empresa,
          segmento: data.segmento || existente.segmento,
          maior_desafio: data.maior_desafio || existente.maior_desafio,
          diagnostico: data.diagnostico || existente.diagnostico,
          lgpd_aceito: data.lgpd_aceito ?? false,
        })
        .eq("id", existente.id)

      if (error) return { success: false, error: error.message }
      return { success: true }
    } else {
      // Cria novo lead
      const { error } = await client.from("leads").insert({
        nome: data.nome,
        email: data.email,
        whatsapp: (data.whatsapp || "").replace(/\D/g, ""),
        empresa: data.empresa || "",
        segmento: data.segmento || "",
        maior_desafio: data.maior_desafio || "",
        origens: [origem],
        status: "novo",
        diagnostico: data.diagnostico || null,
        lgpd_aceito: data.lgpd_aceito ?? false,
      })

      if (error) return { success: false, error: error.message }
      return { success: true }
    }
  } catch (err: any) {
    if (err.name === 'AbortError' || err.message.includes('aborted')) {
      return { success: false, error: "Tempo esgotado" }
    }
    return { success: false, error: err.message }
  }
}

export async function markAsInteressado(email: string): Promise<{ success: boolean; error?: string }> {
  if (!SUPABASE_URL || !SUPABASE_ANON_KEY) {
    console.warn("[markAsInteressado] Supabase env vars not set — skipping. VITE_SUPABASE_URL:", SUPABASE_URL ? "OK" : "MISSING", "VITE_SUPABASE_ANON_KEY:", SUPABASE_ANON_KEY ? "OK" : "MISSING")
    return { success: false, error: "VITE_SUPABASE_URL or VITE_SUPABASE_ANON_KEY not set" }
  }
  try {
    const { createClient } = await import("@supabase/supabase-js")
    const client = createClient(SUPABASE_URL, SUPABASE_ANON_KEY, {
      global: { fetch: (url, options) => {
        const controller = new AbortController()
        const timeout = setTimeout(() => controller.abort(), 10000)
        return fetch(url, { ...options, signal: controller.signal }).finally(() => clearTimeout(timeout))
      }}
    })

    // Primeiro busca origens atuais para não sobrescrever
    const { data: existente } = await client
      .from("leads")
      .select("id, origens")
      .ilike("email", email)
      .maybeSingle()

    const origensAtuais: string[] = existente?.origens ?? []
    const origensUnicas = origensAtuais.includes("programa")
      ? origensAtuais
      : [...origensAtuais, "programa"]

    const { error } = await client
      .from("leads")
      .update({ status: "interessado", origens: origensUnicas })
      .ilike("email", email)
    if (error) {
      console.error("[markAsInteressado] Update error:", error.message, "Code:", error.code, "Details:", error.details)
      return { success: false, error: error.message }
    }
    console.log("[markAsInteressado] Success for email:", email)
    return { success: true }
  } catch (err: any) {
    console.error("[markAsInteressado] Exception:", err)
    return { success: false, error: err.message }
  }
}
